'use client';

import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AddToCartIsland from './AddToCartIsland';
import { AUTH_LOGIN_EVENT } from '@nestingale/auth';
import { useLayout } from '../../providers/LayoutProvider';
import { useAuth } from '@nestingale/auth/hooks';
import cssStyles from '../../[vendor]/product/product.module.scss';

interface ProductPricingIslandProps {
  productId: string;
  productName: string;
  productShopifyId: string;
  availabilityCount?: number;
  /** Product type — needed for the pricing API call. */
  productType?: string;
  /** Brand name — needed for the pricing API call. */
  brand?: string;
}

/**
 * Client island for auth-gated PDP pricing.
 *
 * When dynamic pricing is enabled for the brand, this component replaces
 * the server-rendered price display. It fetches pricing from the
 * `/api/products/pricing` endpoint (which validates the `nest_session`
 * HttpOnly cookie) and renders:
 *   - Price + sale info + free shipping + AddToCart when authenticated
 *   - "Login to view pricing" button when not authenticated (401)
 *   - Loading skeleton while the fetch is in-flight
 *
 * Listens for the `nest:auth:login` custom event to automatically
 * re-fetch pricing after the user logs in without a page reload.
 */
const ProductPricingIsland: React.FC<ProductPricingIslandProps> = ({
  productId,
  productName,
  productShopifyId,
  availabilityCount,
  productType,
  brand,
}) => {
  const [pricing, setPricing] = useState<any>(null);
  const [loaded, setLoaded] = useState(false);
  const [denied, setDenied] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const { globalData } = useLayout();
  const { isAuthenticated, isAuthChecked } = useAuth();
  const userInfo = globalData?.userInfo;
  console.log('[ProductPricingIsland] userInfo:', userInfo);

  // ── Sync with layout auth state ────────────────────────────────────
  useEffect(() => {
    // If the layout context resolves a logged-in user, re-trigger fetch
    if (isAuthChecked && isAuthenticated) {
      console.info('[ProductPricingIsland] Auth detected — triggering fetch.');
      setFetchTrigger((n) => n + 1);
    }
  }, [isAuthChecked, isAuthenticated]);

  // ── Fetch pricing ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthChecked) return;
    if (!isAuthenticated) {
      setDenied(true);
      setLoaded(true);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch('/showroom/api/products/pricing', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            productIds: [productId],
            productType,
            brand,
          }),
        });

        if (cancelled) return;

        if (res.status === 401) {
          console.info('[ProductPricingIsland] Not authenticated — pricing hidden.');
          setDenied(true);
          setLoaded(true);
          return;
        }

        const data = await res.json();
        console.log('[ProductPricingIsland] Pricing data:', data);
        console.log("for-testing-----------------")

        if (!cancelled) {
          setDenied(false);
          setPricing(data.pricing?.[productId] || null);
        }
      } catch (err) {
        console.error('[ProductPricingIsland] Failed to fetch pricing:', err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();

    return () => { cancelled = true; };
  }, [productId, productType, brand, fetchTrigger, isAuthChecked, isAuthenticated]);

  // ── Re-fetch after login, do not help on page load───────────────────────────────────────────
  useEffect(() => {
    const handleAuthLogin = (e) => {
      console.info('[ProductPricingIsland] Auth login detected — re-fetching pricing.', e);
      setLoaded(false);
      setDenied(false);
      setPricing(null);
      setFetchTrigger((n) => n + 1);
    };
    window.addEventListener(AUTH_LOGIN_EVENT, handleAuthLogin);
    return () => window.removeEventListener(AUTH_LOGIN_EVENT, handleAuthLogin);
  }, []);

  // ── Loading state ──────────────────────────────────────────────────
  if (!loaded) {
    return (
      <Box sx={{ mt: 1, mb: 1 }}>
        <Box sx={{ height: '28px', width: '120px', bgcolor: '#f0f0f0', borderRadius: '4px', mb: 1 }} />
        <Box sx={{ height: '40px', width: '100%', bgcolor: '#f0f0f0', borderRadius: '4px' }} />
      </Box>
    );
  }

  // ── Not authenticated — show login prompt ──────────────────────────
  if (denied) {
    return (
      <Box sx={{ mt: 2, mb: 2, textAlign: 'center' }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (typeof window !== 'undefined' && (window as any).openSignIn) {
              (window as any).openSignIn();
            }
          }}
          style={{
            background: 'none',
            border: '1px solid #888',
            borderRadius: '9999px',
            padding: '8px 24px',
            fontSize: '14px',
            fontWeight: 600,
            color: '#333',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'background-color 0.15s ease, color 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#333';
            e.currentTarget.style.color = '#fff';
            e.currentTarget.style.borderColor = '#333';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#333';
            e.currentTarget.style.borderColor = '#888';
          }}
        >
          Login to view pricing
        </button>
      </Box>
    );
  }

  // ── Authenticated — render pricing + AddToCart ─────────────────────
  const price = pricing?.partnerPricing?.calculated?.finalPrice || 0;
  const comparePrice = pricing?.partnerPricing?.calculated?.comparePrice || 0;
  console.log('price ------------->', price);
  console.log('comparePrice ------------->', comparePrice);

  return (
    <>
      {/* Price display */}
      <Box component="div" className={cssStyles.price} sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
        <Box component="div" sx={{ display: 'flex', alignItems: 'center' }}>
          {price > 0 && comparePrice > 0 && price < comparePrice ? (
            <>
              <Box component="span" className={cssStyles.price} sx={{ color: 'primary_extent.mainDark', marginRight: '5px', fontSize: '26px' }}>
                ${price}
              </Box>
              <Box component="span" className={cssStyles.price} sx={{
                textDecoration: 'line-through', textDecorationThickness: '2px', textDecorationColor: '#b0b0b0',
              }}>
                ${comparePrice}
              </Box>
            </>
          ) : price > 0 ? (
            <Box component="span" className={cssStyles.price}>${price}</Box>
          ) : null}
        </Box>
        {price > 0 && comparePrice > 0 && price < comparePrice && (
          <Box component="span" sx={{
            color: 'primary.main', fontSize: '18px', textAlign: 'right', marginLeft: 'auto',
          }}>
            {Math.round(((comparePrice - price) / comparePrice) * 100)}% Off
          </Box>
        )}
      </Box>


      {/* Add to Cart */}
      {price > 0 && (
        <AddToCartIsland
          productId={productId}
          productName={productName}
          productShopifyId={productShopifyId}
          price={price}
          availabilityCount={availabilityCount}
          isDynamicPricing={true}
          productType={productType}
          brand={brand}
        />
      )}
    </>
  );
};

export default ProductPricingIsland;
